import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useData } from '../../context/DataContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { fetchStockPrice, validateSymbol } from '../../services/yahooFinance';
import toast from 'react-hot-toast';

const FundPriceModal = ({ visible, onCancel, fund, onFundAdded }) => {
  const { addFundPrice, updateFundPrice, fundPrices } = useData();

  const [formData, setFormData] = useState({
    name: '',
    symbol: '',
    price: '',
    currency: 'GBP',
  });

  const [isFetchingPrice, setIsFetchingPrice] = useState(false);

  // Close modal on Escape key
  useEscapeKey(onCancel, visible);

  useEffect(() => {
    if (fund) {
      setFormData({
        name: fund.name || '',
        symbol: fund.symbol || '',
        price: fund.price || '',
        currency: fund.currency || 'GBP',
      });
    } else if (visible) {
      setFormData({
        name: '',
        symbol: '',
        price: '',
        currency: 'GBP',
      });
    }
  }, [fund, visible]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.price) {
      return;
    }

    if (parseFloat(formData.price) <= 0) {
      return;
    }

    // Check for duplicate by symbol or name when adding new fund
    if (!fund) {
      const symbolToCheck = formData.symbol?.trim().toUpperCase();
      const nameToCheck = formData.name?.trim().toLowerCase();

      const duplicate = fundPrices.find(f => {
        // Check symbol match (if both have symbols)
        if (symbolToCheck && f.symbol?.trim().toUpperCase() === symbolToCheck) {
          return true;
        }
        // Check exact name match
        if (f.name?.trim().toLowerCase() === nameToCheck) {
          return true;
        }
        return false;
      });

      if (duplicate) {
        toast.error(`A fund with this ${symbolToCheck && duplicate.symbol?.trim().toUpperCase() === symbolToCheck ? 'symbol' : 'name'} already exists`);
        return;
      }
    }

    const fundData = {
      ...formData,
      price: parseFloat(formData.price),
    };

    if (fund) {
      updateFundPrice(fund.id, fundData);
      handleCancel();
    } else {
      const newFund = addFundPrice(fundData);
      handleCancel();
      // Notify parent if callback provided
      if (onFundAdded && newFund) {
        onFundAdded(newFund);
      }
    }
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      symbol: '',
      price: '',
      currency: 'GBP',
    });
    onCancel();
  };

  const handleFetchPrice = async () => {
    if (!formData.symbol || formData.symbol.trim() === '') {
      toast.error('Please enter a symbol first');
      return;
    }

    setIsFetchingPrice(true);
    try {
      const priceData = await fetchStockPrice(formData.symbol.trim());

      // Update form data with fetched information
      setFormData(prev => ({
        ...prev,
        name: priceData.name || prev.name, // Only update name if it's empty or if Yahoo provides a better name
        price: priceData.price.toString(),
        currency: priceData.currency,
      }));

      toast.success(`Price fetched successfully: ${priceData.currency} ${priceData.price}`);
    } catch (error) {
      console.error('Failed to fetch price:', error);
      toast.error(error.message || 'Failed to fetch price from Yahoo Finance');
    } finally {
      setIsFetchingPrice(false);
    }
  };

  return (
    <Transition appear show={visible} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleCancel}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-4 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-base font-semibold text-gray-900 mb-2">
                  {fund ? 'Edit Fund Price' : 'Add Fund'}
                </Dialog.Title>

                <hr className="mb-3 border-gray-200" />

                <form onSubmit={handleSubmit} className="space-y-3">
                  {/* Fund Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Fund Name <span className="text-red-600">*</span>
                    </label>
                    <input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Vanguard FTSE All-World"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  {/* Symbol/Ticker */}
                  <div>
                    <label htmlFor="symbol" className="block text-sm font-medium text-gray-700 mb-1">
                      Symbol/Ticker (Optional)
                    </label>
                    <div className="flex gap-2">
                      <input
                        id="symbol"
                        type="text"
                        value={formData.symbol}
                        onChange={(e) => setFormData({ ...formData, symbol: e.target.value })}
                        placeholder="e.g., VWRL"
                        className="block flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        type="button"
                        onClick={handleFetchPrice}
                        disabled={isFetchingPrice || !formData.symbol.trim()}
                        className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ backgroundColor: isFetchingPrice ? '#6b7280' : '#059669' }}
                        onMouseEnter={(e) => !isFetchingPrice && (e.currentTarget.style.backgroundColor = '#047857')}
                        onMouseLeave={(e) => !isFetchingPrice && (e.currentTarget.style.backgroundColor = '#059669')}
                      >
                        {isFetchingPrice ? (
                          <ArrowPathIcon className="w-4 h-4 animate-spin" />
                        ) : (
                          <ArrowPathIcon className="w-4 h-4" />
                        )}
                        {isFetchingPrice ? 'Fetching...' : 'Fetch Price'}
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {/* Price */}
                    <div>
                      <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                        Price <span className="text-red-600">*</span>
                      </label>
                      <input
                        id="price"
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    {/* Currency */}
                    <div>
                      <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">
                        Currency <span className="text-red-600">*</span>
                      </label>
                      <select
                        id="currency"
                        value={formData.currency}
                        onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                        className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="GBP">GBP (£)</option>
                        <option value="USD">USD ($)</option>
                        <option value="EUR">EUR (€)</option>
                      </select>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 justify-end pt-2">
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                      style={{ backgroundColor: '#012E58' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
                    >
                      {fund ? 'Update' : 'Add'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default FundPriceModal;
